import { observable, computed, makeObservable } from 'mobx';
import { createTransformer } from 'mobx-utils';

import { EntityList, Job } from '@vromo/models';
import { Vehicle } from '@vromo/models';
import { RootStore } from './RootStore';
import { meanBy, sortBy } from 'lodash';

function deg2rad(deg: number) {
    return deg * (Math.PI / 180);
}

interface LatLon {
    lat: number;
    lon: number;
}

/** uses the Haversine formula */
export function calculateDistance(
    { lat: lat1, lon: lon1 }: LatLon,
    { lat: lat2, lon: lon2 }: LatLon
) {
    const R = 6371; // ~radius of the earth (km)

    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // distance in km
}


export const closestTo = (vehicles: Vehicle[], jobs: Job[]): Vehicle[] => {
    const jobLocation = {
        lat: meanBy(jobs, (it) => it.tasks!.length && it.tasks!.first()!.zone!.coords.lat),
        lon: meanBy(jobs, (it) => it.tasks!.length && it.tasks!.first()!.zone!.coords.lon)
    };

    return sortBy(vehicles, [
        (vehicle) => {
            return vehicle.location ?
                calculateDistance(jobLocation, vehicle.location!) :
                Infinity;
        }
    ]);
}



export default class VehicleStore {
    rootStore: RootStore;
    vehicles: EntityList<Vehicle> = new EntityList(Vehicle);
    loaded = false;

    /** may fail id the item id record has been deleted from the store or from the Backend */
    withId = (id: number): Vehicle | undefined => this.vehicles._map.get(id);

    withDriver = createTransformer((driver: number) =>
        this.vehicles.filter((it) => !!it.driver && it.driver.id === driver)
    );

    withGroup = createTransformer((group: number) =>
        this.vehicles.filter((it) => it.group === group)
    );

    filteredWithGroup = createTransformer((group: number) =>
        this.filteredByGroup.get(group) || []);

    get filteredByGroup(): Map<number, Vehicle[]> {
        const map = new Map<number, Array<Vehicle>>();

        this.filteredVehicles.forEach((vehicle) => {
            if (vehicle.group) {
                const entry = map.get(vehicle.group!);

                if (entry) {
                    entry.push(vehicle);
                } else {
                    map.set(vehicle.group!, [vehicle]);
                }
            }
        });
        return map;
    }

    hasSyntheticInGroup = createTransformer((group: number) =>
        this.vehicles.filter((it) => it.group === group).some(({ synthetic }) => synthetic)
    );

    get availableDrivers(): Vehicle[] {
        return this.vehicles.filter((v) => v.available);
    }

    get driverVehicleMap(): Map<number, Array<number>> {
        return new Map(
            Object.entries(
                this.vehicles.groupBy((v: Vehicle) => v.driver.id)
            ).map(([driverId, vehicles]) => [
                parseInt(driverId),
                vehicles.map((v: Vehicle) => v.id)
            ])
        );
    }

    get filteredVehicles(): Vehicle[] {
        const { viewStore } = this.rootStore;

        const { teams, search, offline } = viewStore.filters.drivers;

        return this.sortedVehicles.filter(
            (v) =>
                (teams.length ? teams.some((t) => t === v.group) : true) &&
                (offline || (v.lastAvailable && v.lastAvailable > Date.now())) &&
                (v.driver?.displayName?.toLowerCase()?.includes(search.toLowerCase()) ||
                    v.driver?.contact?.phone?.includes(search))
        );
    }

    /** sorted vehicles by vehicle name / driver contact initals */
    get sortedVehicles(): Vehicle[] {
        if (!this.loaded)
            return []
            
        return this.vehicles.sortBy([(vehicle: Vehicle) => vehicle.name!.toLowerCase(), 'driver.initials'])
    }

    constructor(context: RootStore) {
        makeObservable(this, {
            vehicles: observable,
            sortedVehicles: computed,
            loaded: observable,
            availableDrivers: computed,
            driverVehicleMap: computed,
            filteredVehicles: computed,
            filteredByGroup: computed,
        });

        this.rootStore = context;
    }
}
